/**
 * F1b — drain worker for the local-edit queue.
 *
 * Two entry points:
 *   - `drainOnce()` — pure async function. Tries each due row exactly once.
 *     Useful from event handlers and tests.
 *   - `useLocalEditQueueDrain()` — React hook that wires `drainOnce` to
 *     `online` events, window focus, and a 60s tick. Mount once at root.
 *
 * Per-item disposition:
 *   - success → remove from queue.
 *   - 412/409 conflict → keep in queue; dispatch a 'repancy:queue-conflict'
 *     CustomEvent on window for ConflictDialog to pick up. (No direct UI
 *     coupling here — leaf module.)
 *   - 401/403 access-denied → keep in queue; dispatch
 *     'repancy:queue-access-denied'. Same reason as conflict.
 *   - transient → recordAttempt (bumps attempts + nextAttemptAt). Abandons
 *     after ABANDON_AFTER_ATTEMPTS (10) and dispatches 'repancy:queue-abandoned'.
 *
 * The drain itself doesn't import lib/graph/sharepoint to keep the queue
 * leaf-pure; the SPA registers a kind-specific replayer via
 * `setKindReplayer(kind, fn)`. Today only 'save-back' has a replayer
 * (registered in sharepoint.ts on module load? — no, that'd circular import.
 * Registered in main.tsx when the SPA boots).
 */

import {
  type PendingSharePointWrite,
  type PendingKind,
  type GraphErrorKind,
  listDue,
  remove,
  recordAttempt,
} from './localEditQueue';

export interface QueueEventDetail {
  row: PendingSharePointWrite;
  errorKind: GraphErrorKind;
  errorMessage: string;
}

interface ErrorWithStatus {
  statusCode?: number;
  status?: number;
}

type Replayer = (row: PendingSharePointWrite) => Promise<void>;

const replayers = new Map<PendingKind, Replayer>();

export function setKindReplayer(kind: PendingKind, fn: Replayer): void {
  replayers.set(kind, fn);
}

export function __clearReplayersForTests(): void {
  replayers.clear();
}

function classifyErrorKind(err: unknown): { errorKind: GraphErrorKind; status: number | undefined } {
  const e = err as ErrorWithStatus;
  const status = e?.statusCode ?? e?.status;
  if (status === 409 || status === 412) return { errorKind: 'conflict', status };
  if (status === 401 || status === 403) return { errorKind: 'access-denied', status };
  if (status === undefined || status === 408 || status === 425 || status === 429 || (status >= 500 && status <= 504)) {
    return { errorKind: 'transient', status };
  }
  return { errorKind: 'fatal', status };
}

function dispatch(eventName: string, detail: QueueEventDetail): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  } catch {
    // ignore (jsdom without CustomEvent)
  }
}

export interface DrainResult {
  attempted: number;
  succeeded: number;
  conflicts: number;
  accessDenied: number;
  abandoned: number;
  transient: number;
  skippedNoReplayer: number;
}

/**
 * Attempt every due row exactly once. Returns a summary so the caller
 * (or tests) can verify behaviour.
 */
export async function drainOnce(now: number = Date.now()): Promise<DrainResult> {
  const due = await listDue(now);
  const result: DrainResult = {
    attempted: due.length,
    succeeded: 0,
    conflicts: 0,
    accessDenied: 0,
    abandoned: 0,
    transient: 0,
    skippedNoReplayer: 0,
  };

  for (const row of due) {
    const replayer = replayers.get(row.kind);
    if (!replayer) {
      result.skippedNoReplayer++;
      continue;
    }
    try {
      await replayer(row);
      await remove(row.id);
      result.succeeded++;
    } catch (err) {
      const { errorKind, status } = classifyErrorKind(err);
      const errorMessage = err instanceof Error ? err.message : String(err);

      if (errorKind === 'conflict') {
        result.conflicts++;
        dispatch('repancy:queue-conflict', { row, errorKind, errorMessage });
        // Don't bump attempts — wait for the user to resolve via ConflictDialog.
        continue;
      }
      if (errorKind === 'access-denied') {
        result.accessDenied++;
        dispatch('repancy:queue-access-denied', { row, errorKind, errorMessage });
        continue;
      }
      if (errorKind === 'fatal') {
        // 400 / 404 etc. — won't ever succeed. Drop with a notification.
        result.abandoned++;
        await remove(row.id);
        dispatch('repancy:queue-abandoned', { row, errorKind, errorMessage });
        continue;
      }
      // Transient
      const outcome = await recordAttempt({
        id: row.id,
        errorKind,
        errorMessage: `${status ?? 'network'}: ${errorMessage}`,
      });
      if ('abandoned' in outcome) {
        result.abandoned++;
        dispatch('repancy:queue-abandoned', { row: outcome.abandoned, errorKind, errorMessage });
      } else {
        result.transient++;
      }
    }
  }

  return result;
}
